import React, { ReactElement } from 'react';
import { TooltipProps as ReachTooltipProps, TooltipPopup, useTooltip } from '@reach/tooltip';
import clsx from 'clsx';

export interface TooltipProps extends ReachTooltipProps {
  disableWrap?: boolean;
}

type PRect = Partial<DOMRect> & {
  readonly bottom: number;
  readonly height: number;
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly width: number;
};

type Position = (targetRect?: PRect | null, popoverRect?: PRect | null) => React.CSSProperties;

const centered: Position = (triggerRect, tooltipRect) => {
  if (!triggerRect || !tooltipRect) {
    return {};
  }
  // Adds a small gap between the trigger element and the tooltip popup.
  const popupOffset = 6;
  // Centre the popup, but ensure it doesn't go offscreen.
  const triggerCenter = triggerRect.left + triggerRect.width / 2;
  const left = triggerCenter - tooltipRect.width / 2;
  const maxLeft = window.innerWidth - tooltipRect.width;

  return {
    left: Math.min(Math.max(0, left), maxLeft) + window.scrollX,
    top:
      triggerRect.top >= tooltipRect.height + popupOffset
        ? triggerRect.top - (popupOffset + tooltipRect.height) + window.scrollY
        : triggerRect.bottom + popupOffset + window.scrollY,
  };
};

/**
 * Tooltip component, based on Reach's Tooltip component, with custom styling
 * (as per the demo/documentation: https://reach.tech/tooltip/#demos)
 */
export const Tooltip: React.FC<
  TooltipProps & {
    children: ReactElement<any>;
    className?: string;
  }
> = ({ children, label, ariaLabel, disableWrap = false, className, ...rest }) => {
  const [trigger, tooltip] = useTooltip();

  return (
    <>
      {React.cloneElement(children, trigger)}
      <TooltipPopup
        {...tooltip}
        {...rest}
        label={label}
        aria-label={ariaLabel}
        className={clsx(
          `absolute pointer-events-none max-w-xs z-99999`,
          `bg-content-primary shadow-md rounded-md px-3 py-2`,
          `text-xs text-content-inverted leading-relaxed font-light`,
          disableWrap && `max-w-none whitespace-nowrap`,
          className,
        )}
        position={centered}
      />
    </>
  );
};
